import { __rest } from "tslib";
import { BaseSocialLogin } from './base';
import { createUserCancelledError, inferUserCancelledError } from './errors';
import { listenForOAuthResult } from './oauth-popup-bridge';
export class TelegramSocialLogin extends BaseSocialLogin {
    constructor() {
        super(...arguments);
        this.botId = null;
        this.redirectUrl = null;
        this.origin = null;
        this.requestAccess = 'write';
        this.languageCode = null;
        this.TOKENS_KEY = 'capgo_social_login_telegram_session_v1';
        this.STATE_PREFIX = 'capgo_social_login_telegram_state_';
        this.SESSION_TTL_MS = 24 * 60 * 60 * 1000; // 24h session window based on Telegram auth_date
    }
    initialize(botId, requestAccess, redirectUrl, origin, languageCode) {
        this.botId = botId;
        this.requestAccess = requestAccess !== null && requestAccess !== void 0 ? requestAccess : 'write';
        this.redirectUrl = redirectUrl !== null && redirectUrl !== void 0 ? redirectUrl : null;
        this.origin = origin !== null && origin !== void 0 ? origin : null;
        this.languageCode = languageCode !== null && languageCode !== void 0 ? languageCode : null;
    }
    async login(options) {
        var _a, _b, _c, _d, _e;
        if (!this.botId) {
            throw new Error('Telegram botId is not configured. Call initialize() first.');
        }
        const redirectUri = (_b = (_a = options.redirectUrl) !== null && _a !== void 0 ? _a : this.redirectUrl) !== null && _b !== void 0 ? _b : window.location.origin + window.location.pathname;
        const state = (_c = options.state) !== null && _c !== void 0 ? _c : this.generateState();
        const requestAccess = (_d = options.requestAccess) !== null && _d !== void 0 ? _d : this.requestAccess;
        const origin = (_e = this.origin) !== null && _e !== void 0 ? _e : this.getOriginFromRedirect(redirectUri);
        if (!this.isHttpOrigin(origin)) {
            throw new Error('Telegram origin must be a valid http(s) URL registered with BotFather. Pass origin when redirectUrl is not http(s).');
        }
        const returnTo = this.appendStateToRedirect(redirectUri, state);
        this.persistPendingLogin(state, {
            redirectUri,
            requestAccess,
        });
        localStorage.setItem(BaseSocialLogin.OAUTH_STATE_KEY, JSON.stringify({ provider: 'telegram', state }));
        const authUrl = this.buildAuthUrl(origin, requestAccess, returnTo);
        const popup = this.openPopup(authUrl);
        return new Promise((resolve, reject) => {
            if (!popup) {
                this.clearPendingTelegramState(state);
                reject(new Error('Unable to open login window. Please allow popups.'));
                return;
            }
            let settled = false;
            let timeoutHandle = 0;
            let popupClosedInterval = 0;
            let removeOAuthListener = null;
            const cleanup = (shouldClose = false) => {
                clearTimeout(timeoutHandle);
                clearInterval(popupClosedInterval);
                if (removeOAuthListener) {
                    removeOAuthListener();
                    removeOAuthListener = null;
                }
                this.clearPendingTelegramState(state);
                if (shouldClose) {
                    try {
                        popup.close();
                    }
                    catch (_a) {
                        // ignore if already closed
                    }
                }
            };
            const settle = (shouldClose, action) => {
                if (settled)
                    return;
                settled = true;
                cleanup(shouldClose);
                action();
            };
            removeOAuthListener = listenForOAuthResult('telegram', state, {
                onResponse: (data) => {
                    if ((data === null || data === void 0 ? void 0 : data.provider) && data.provider !== 'telegram') {
                        return;
                    }
                    const _a = data, { provider: _ignoredProvider, type: _ignoredType, _ts: _ignoredTs } = _a, payload = __rest(_a, ["provider", "type", "_ts"]);
                    settle(true, () => {
                        resolve({
                            provider: 'telegram',
                            result: payload,
                        });
                    });
                },
                onError: (error, code) => {
                    const message = error || 'Telegram login was cancelled.';
                    settle(true, () => {
                        reject(code === 'USER_CANCELLED' ? createUserCancelledError(message) : inferUserCancelledError(message));
                    });
                },
            });
            timeoutHandle = window.setTimeout(() => {
                settle(true, () => {
                    reject(new Error('Telegram login timed out.'));
                });
            }, 300000);
            popupClosedInterval = window.setInterval(() => {
                try {
                    if (popup.closed) {
                        settle(false, () => {
                            reject(createUserCancelledError('Telegram login window was closed.'));
                        });
                    }
                }
                catch (_a) {
                    clearInterval(popupClosedInterval);
                }
            }, 1000);
        });
    }
    async logout() {
        localStorage.removeItem(this.TOKENS_KEY);
    }
    async isLoggedIn() {
        const session = this.getStoredSession();
        if (!session) {
            return { isLoggedIn: false };
        }
        const isValid = session.expiresAt > Date.now();
        if (!isValid) {
            localStorage.removeItem(this.TOKENS_KEY);
        }
        return { isLoggedIn: isValid };
    }
    async getAuthorizationCode() {
        throw new Error('getAuthorizationCode is not available for Telegram.');
    }
    async refresh() {
        throw new Error('Telegram refresh is not supported. Call login() again.');
    }
    async handleOAuthRedirect(url, expectedState) {
        var _a;
        const params = url.searchParams;
        const stateFromUrl = params.get('state');
        if (!stateFromUrl) {
            return null;
        }
        if (expectedState && expectedState !== stateFromUrl) {
            return { error: 'Telegram login session expired or state mismatch.' };
        }
        const pending = this.consumePendingLogin(stateFromUrl);
        if (!pending) {
            localStorage.removeItem(BaseSocialLogin.OAUTH_STATE_KEY);
            return { error: 'Telegram login session expired or state mismatch.' };
        }
        const error = params.get('error');
        if (error) {
            localStorage.removeItem(BaseSocialLogin.OAUTH_STATE_KEY);
            return { error: params.get('error_description') || error };
        }
        const id = params.get('id');
        const authDateRaw = params.get('auth_date');
        const hash = params.get('hash');
        if (!id || !authDateRaw || !hash) {
            localStorage.removeItem(BaseSocialLogin.OAUTH_STATE_KEY);
            return { error: 'Telegram login payload is incomplete.' };
        }
        const profile = {
            id,
            firstName: (_a = params.get('first_name')) !== null && _a !== void 0 ? _a : '',
            lastName: params.get('last_name'),
            username: params.get('username'),
            photoUrl: params.get('photo_url'),
        };
        const authDate = Number(authDateRaw);
        if (!Number.isInteger(authDate)) {
            localStorage.removeItem(BaseSocialLogin.OAUTH_STATE_KEY);
            return { error: 'Telegram auth_date is invalid.' };
        }
        const result = {
            profile,
            authDate,
            hash,
            requestAccess: pending.requestAccess,
        };
        this.persistSession({
            expiresAt: authDate * 1000 + this.SESSION_TTL_MS,
        });
        localStorage.removeItem(BaseSocialLogin.OAUTH_STATE_KEY);
        return {
            provider: 'telegram',
            result,
        };
    }
    buildAuthUrl(origin, requestAccess, returnTo) {
        var _a;
        const params = new URLSearchParams({
            bot_id: (_a = this.botId) !== null && _a !== void 0 ? _a : '',
            origin,
            request_access: requestAccess,
            return_to: returnTo,
            embed: '1',
            mobile: '1',
        });
        if (this.languageCode) {
            params.set('lang', this.languageCode);
        }
        return `https://oauth.telegram.org/auth?${params.toString()}`;
    }
    openPopup(authUrl) {
        const width = 500;
        const height = 650;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;
        return window.open(authUrl, 'TelegramLogin', `width=${width},height=${height},left=${left},top=${top},popup=1`);
    }
    appendStateToRedirect(redirectUri, state) {
        try {
            const redirect = new URL(redirectUri);
            redirect.searchParams.set('state', state);
            return redirect.toString();
        }
        catch (_a) {
            return redirectUri;
        }
    }
    isHttpOrigin(value) {
        if (!value) {
            return false;
        }
        try {
            const url = new URL(value);
            return url.protocol === 'http:' || url.protocol === 'https:';
        }
        catch (_a) {
            return false;
        }
    }
    getOriginFromRedirect(redirectUri) {
        return this.isHttpOrigin(redirectUri) ? new URL(redirectUri).origin : undefined;
    }
    persistSession(session) {
        localStorage.setItem(this.TOKENS_KEY, JSON.stringify(session));
    }
    getStoredSession() {
        const raw = localStorage.getItem(this.TOKENS_KEY);
        if (!raw) {
            return null;
        }
        try {
            return JSON.parse(raw);
        }
        catch (err) {
            console.warn('Failed to parse stored Telegram session', err);
            return null;
        }
    }
    persistPendingLogin(state, payload) {
        localStorage.setItem(`${this.STATE_PREFIX}${state}`, JSON.stringify(payload));
    }
    consumePendingLogin(state) {
        const key = `${this.STATE_PREFIX}${state}`;
        const raw = localStorage.getItem(key);
        localStorage.removeItem(key);
        if (!raw) {
            return null;
        }
        try {
            return JSON.parse(raw);
        }
        catch (err) {
            console.warn('Failed to parse pending Telegram login payload', err);
            return null;
        }
    }
    clearPendingTelegramState(state) {
        try {
            localStorage.removeItem(`${this.STATE_PREFIX}${state}`);
            const raw = localStorage.getItem(BaseSocialLogin.OAUTH_STATE_KEY);
            if (!raw) {
                return;
            }
            const parsed = JSON.parse(raw);
            if (parsed.provider === 'telegram' && parsed.state === state) {
                localStorage.removeItem(BaseSocialLogin.OAUTH_STATE_KEY);
            }
        }
        catch (_a) {
            // ignore storage errors
        }
    }
    generateState() {
        return [...crypto.getRandomValues(new Uint8Array(16))].map((b) => b.toString(16).padStart(2, '0')).join('');
    }
}
//# sourceMappingURL=telegram-provider.js.map